/**
 * Tâches 1146 et 1148 (Eric 2026-06-16) — Audit des destinataires + bandeau
 * « incidents silencieux ».
 *
 * Après trois corrections de routage, la seule réponse crédible à « est-ce que
 * Vincent reçoit encore quelque chose ? » est un écran qui recalcule tout en
 * direct, organisation par organisation. Les lignes sans destinataire portent
 * le motif du silence : « en pause », « aucune adresse enregistrée », « jamais
 * vérifiée ».
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export const RecipientAuditPanel = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [onlySilent, setOnlySilent] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/system-notifications/audit`, {
        withCredentials: true,
      });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  if (!data) return null;
  const rows = (data.rows || []).filter(
    (r) => !onlySilent || !r.recipients?.length
  );
  const silent = data.silent_incidents || [];

  return (
    <section className="mb-6" data-testid="recipient-audit">
      {silent.length > 0 && (
        <div
          className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3"
          data-testid="silent-incident-alert"
        >
          <div className="flex items-start gap-3">
            <Warning size={20} className="mt-0.5 shrink-0 text-red-600" />
            <div className="text-sm text-red-900">
              <p className="font-semibold">
                {silent.length} incident(s) ouvert(s) depuis plus de{" "}
                {data.silent_after_hours} h sans qu'aucun courriel ne soit parti
              </p>
              <ul className="mt-1 space-y-0.5 text-red-800">
                {silent.map((i) => (
                  <li key={i.key} data-testid={`silent-incident-${i.key}`}>
                    <strong>{i.org_name}</strong> — {i.title} ({i.hours_open} h,{" "}
                    {i.occurrences} occurrence(s)) · {i.reason}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <h2 className="text-base font-semibold text-zinc-900">
            Audit des destinataires — qui recevrait quoi aujourd'hui
          </h2>
          <p className="text-xs text-zinc-500">
            Recalculé en direct à l'ouverture de cette page. Une ligne sans
            destinataire indique pourquoi personne ne recevrait rien.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
          <input
            type="checkbox"
            checked={onlySilent}
            onChange={(e) => setOnlySilent(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300 text-[#002FA7]"
            data-testid="audit-only-silent"
          />
          N'afficher que les familles sans destinataire
        </label>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs">
            <tr>
              <th className="text-left px-3 py-2">Organisation</th>
              <th className="text-left px-3 py-2">Famille</th>
              <th className="text-left px-3 py-2">Réglé dans</th>
              <th className="text-left px-3 py-2">Destinataires effectifs</th>
              <th className="text-left px-3 py-2">Dernier envoi</th>
            </tr>
          </thead>
          <tbody data-testid="audit-rows">
            {rows.map((r) => (
              <tr
                key={`${r.org_id}-${r.family}`}
                className="border-t border-zinc-100"
                data-testid={`audit-row-${r.org_id}-${r.family}`}
              >
                <td className="px-3 py-2 text-zinc-600">{r.org_name}</td>
                <td className="px-3 py-2">{r.family_label}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">
                  {r.governed_by === "sentinelle"
                    ? "Alertes courriel (Sentinelle)"
                    : "Notifications système"}
                </td>
                <td className="px-3 py-2">
                  {r.recipients?.length ? (
                    <span className="text-emerald-700">
                      {r.recipients.join(", ")}
                    </span>
                  ) : (
                    <span className="text-zinc-500">
                      Personne — {r.reason}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">
                  {r.last_sent_at
                    ? r.last_sent_at.slice(0, 16).replace("T", " ")
                    : "jamais"}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-sm text-zinc-500"
                  data-testid="audit-empty"
                >
                  Toutes les familles ont au moins un destinataire.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default RecipientAuditPanel;
